/**
 * «План и ход агроцикла» — один виджет вместо двух (план по этапам и
 * хронология работ, решение владельца 08.10). Два вида (agroai-0e8, замечание
 * владельца: кликабельные этапы снизу и детализация по нажатию занимали
 * слишком много места):
 * - «Кратко» (по умолчанию) — только шкала этапов с долей отчётов;
 * - «Подробно» — та же шкала и под ней календарь работ (`WindowTimeline`):
 *   этапы группами, у каждой работы своя полоса окна, ход отчётов; в конце —
 *   лента событий агроцикла. Нажатие на этап шкалы открывает «Подробно» с этим
 *   этапом.
 *
 * Менеджер (`canEdit`) правит план прямо на календаре (agroai-b48, M3): тащит
 * полосу — окно работы сдвигается и сохраняется сразу с «Отменить»; по запущенной
 * работе вместо этого открывается «Перенести» кампании. Хоким видит календарь
 * только для чтения. Вся правка — `usePlanEditing`.
 *
 * Числа — сервера: окна и состояние работ плана — `/agrocycles/{key}`, ход
 * запущенной работы — итоги её кампании (`/season/picture`). Клиент только
 * сводит два ответа по кампании и раскладывает их по шкале.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Ban, CircleCheck, CircleDashed, CircleDot, Clock3, GanttChart, Plus, Rows3 } from 'lucide-react';
import { Card } from '../../ui/Card';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { WindowTimeline, type TimelineBar, type TimelineGroup } from '../../ui/timeline/WindowTimeline';
import { useAuth } from '../../../auth/context';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import { countOf, dateShort, pctAxis, windowLabel, WORDS } from '../../lib/format';
import type { Work } from '../../data/types';
import { planInfo, type AgrocycleState } from '../agrocycle/AgrocyclePlan';
import type { WorkFacts } from '../agrocycle/PlanWorkDrawer';
import { barKey, workDrag } from '../agrocycle/planEdit';
import { usePlanEditing } from '../agrocycle/usePlanEditing';
import { assignLanes, buildStages, focusStage, makeAxis, type ItemState, type PlanItem, type StageCard, type StageState } from './cyclePlanModel';
import './cyclePage.css';

const m = defineMessages({
  title: { id: 'cycle.page.plan.title', defaultMessage: 'План и ход агроцикла', description: 'Заголовок виджета: этапы плана агроцикла и работы на шкале времени' },
  aside: {
    id: 'cycle.page.plan.aside',
    defaultMessage: '{stages} · {works} · закрыто {done} · идёт {active} · впереди {ahead}',
    description: 'Сводка справа в заголовке виджета плана; {stages} — «7 этапов», {works} — «40 работ», остальные — числа строкой',
  },
  stageCount: {
    id: 'cycle.page.plan.stageCount',
    defaultMessage: '{count, plural, one {# этап} few {# этапа} many {# этапов} other {# этапа}}',
    description: 'Число этапов плана; {count} — число',
  },
  axisAria: { id: 'cycle.page.plan.axisAria', defaultMessage: 'Этапы агроцикла на шкале времени', description: 'aria-label шкалы этапов агроцикла' },
  today: { id: 'cycle.page.plan.today', defaultMessage: 'срез {date}', description: 'Подпись вертикальной линии на шкале: дата среза; {date} — дата' },
  stageDone: { id: 'cycle.page.plan.stage.done', defaultMessage: 'закрыт', description: 'Состояние этапа: окно этапа закрыто; строчными' },
  stageActive: { id: 'cycle.page.plan.stage.active', defaultMessage: 'идёт', description: 'Состояние этапа: дата среза внутри окна этапа; строчными' },
  stageAhead: { id: 'cycle.page.plan.stage.ahead', defaultMessage: 'впереди', description: 'Состояние этапа: окно этапа ещё не открылось; строчными' },
  stageNoDates: { id: 'cycle.page.plan.stage.noDates', defaultMessage: 'без дат', description: 'Этап без дат: на шкале не показан' },
  stageReported: { id: 'cycle.page.plan.stage.reported', defaultMessage: 'выполнено {pct}', description: 'Доля выполненного на этапе (по га работ запущенных кампаний, без «в процессе»; как KPI цикла); {pct} — «78,0 %»' },
  stageBarTitle: {
    id: 'cycle.page.plan.stage.barTitle',
    defaultMessage: '{name}: {window}, {state}',
    description: 'Подсказка полосы этапа на шкале; {name} — этап, {window} — окно дат, {state} — состояние',
  },
  noDatesNote: {
    id: 'cycle.page.plan.noDatesNote',
    defaultMessage: 'Без дат на шкале: {names}.',
    description: 'Подпись под шкалой: этапы без дат, которые на шкале не показаны; {names} — перечень названий',
  },
  stageHead: {
    id: 'cycle.page.plan.stageHead',
    defaultMessage: '{order}. {name}',
    description: 'Заголовок раскрытого этапа; {order} — номер этапа, {name} — название',
  },
  itemDone: { id: 'cycle.page.plan.item.done', defaultMessage: 'закрыта', description: 'Состояние карточки работы: окно закрыто; строчными' },
  itemActive: { id: 'cycle.page.plan.item.active', defaultMessage: 'идёт', description: 'Состояние карточки работы: окно идёт; строчными' },
  itemSoon: { id: 'cycle.page.plan.item.soon', defaultMessage: 'скоро', description: 'Состояние карточки работы: окно наступает в ближайшие дни, кампании ещё нет; строчными' },
  itemAhead: { id: 'cycle.page.plan.item.ahead', defaultMessage: 'впереди', description: 'Состояние карточки работы: окно впереди; строчными' },
  itemCancelled: { id: 'cycle.page.plan.item.cancelled', defaultMessage: 'убрана из плана', description: 'Состояние карточки работы: работа отменена менеджером; строчными' },
  itemReported: { id: 'cycle.page.plan.item.reported', defaultMessage: 'выполнено {pct}', description: 'Доля выполненного по работе плана (по га работ без «в процессе», без площади — по числу); {pct} — «78,0 %»' },
  itemOverdue: { id: 'cycle.page.plan.item.overdue', defaultMessage: 'после срока {count}', description: 'Число работ на полях после срока без отчёта по работе плана; {count} — «12 работ на полях»' },
  itemAnnounced: { id: 'cycle.page.plan.item.announced', defaultMessage: 'кампания объявлена, окно впереди', description: 'Карточка работы плана: кампания уже запущена менеджером, но окно работы ещё не открылось' },
  itemNoCampaign: { id: 'cycle.page.plan.item.noCampaign', defaultMessage: 'кампании ещё нет', description: 'Карточка работы плана без кампании: работ на полях ещё нет' },
  itemApprox: { id: 'cycle.page.plan.item.approx', defaultMessage: '≈ {window}', description: 'Окно работы примерное; {window} — окно дат' },
  itemIrrigation: { id: 'cycle.page.plan.item.irrigation', defaultMessage: 'полив {no}', description: 'Пометка карточки: порядковый номер полива; {no} — номер' },
  itemAdded: { id: 'cycle.page.plan.item.added', defaultMessage: 'добавлена в агроцикле', description: 'Пометка карточки: работа добавлена в агроцикле, а не из шаблона' },
  itemOptional: { id: 'cycle.page.plan.item.optional', defaultMessage: 'по желанию', description: 'Пометка карточки: работа не обязательная' },
  noItems: { id: 'cycle.page.plan.noItems', defaultMessage: 'В этапе нет работ.', description: 'Пустой этап плана' },
  modeAria: { id: 'cycle.page.plan.modeAria', defaultMessage: 'Подробность виджета плана', description: 'aria-label переключателя «Кратко / Подробно» в шапке виджета плана агроцикла' },
  modeBrief: { id: 'cycle.page.plan.mode.brief', defaultMessage: 'Кратко', description: 'Переключатель вида виджета плана: только шкала этапов' },
  modeDetail: { id: 'cycle.page.plan.mode.detail', defaultMessage: 'Подробно', description: 'Переключатель вида виджета плана: этапы разделами, внутри работы с окнами и ходом отчётов' },
  briefHint: { id: 'cycle.page.plan.briefHint', defaultMessage: 'Нажмите на этап — откроются его работы с окнами и ходом отчётов.', description: 'Подсказка под шкалой этапов в кратком виде виджета плана' },
  empty: { id: 'cycle.page.plan.empty', defaultMessage: 'Плана нет', description: 'Заголовок пустого состояния виджета плана' },
  timelineAria: { id: 'cycle.page.plan.timelineAria', defaultMessage: 'План агроцикла: этапы, работы и окна по календарю', description: 'Доступное имя календаря работ плана агроцикла (aria-label)' },
  legend: {
    id: 'cycle.page.plan.legend',
    defaultMessage: 'Штриховка — примерный срок, бледная полоса — работа по желанию, пунктир — отсчёт от другой работы. Нажмите на работу — откроется её карточка.',
    description: 'Подпись над календарём работ плана агроцикла: как читать полосы',
  },
  legendEdit: {
    id: 'cycle.page.plan.legendEdit',
    defaultMessage: 'Тащите полосу — окно работы сдвинется и сохранится сразу; по работе с кампанией откроется «Перенести».',
    description: 'Подсказка менеджеру над календарём работ плана агроцикла: что можно делать мышью',
  },
  addWork: { id: 'cycle.page.plan.addWork', defaultMessage: 'Добавить работу в этап «{stage}»', description: 'aria-label и подсказка кнопки «+» у этапа в календаре плана (менеджер); {stage} — название этапа' },
  hintAfterStart: { id: 'cycle.page.plan.hint.afterStart', defaultMessage: 'через {days} дн. после начала «{name}»', description: 'Подсказка над полосой работы, окно которой отсчитывается от начала другой; {days} — число дней, {name} — другая работа' },
  hintAfterEnd: { id: 'cycle.page.plan.hint.afterEnd', defaultMessage: 'через {days} дн. после конца «{name}»', description: 'Подсказка над полосой работы, окно которой отсчитывается от конца другой; {days} — число дней, {name} — другая работа' },
  hintBeforeStart: { id: 'cycle.page.plan.hint.beforeStart', defaultMessage: 'за {days} дн. до начала «{name}»', description: 'Подсказка над полосой работы, которая стоит раньше начала другой; {days} — число дней, {name} — другая работа' },
  hintBeforeEnd: { id: 'cycle.page.plan.hint.beforeEnd', defaultMessage: 'за {days} дн. до конца «{name}»', description: 'Подсказка над полосой работы, которая стоит раньше конца другой; {days} — число дней, {name} — другая работа' },
  hintLaunched: { id: 'cycle.page.plan.hint.launched', defaultMessage: 'идёт кампания: срок меняет «Перенести»', description: 'Подсказка над полосой работы с запущенной кампанией (менеджер): как менять срок' },
  projectionNote: {
    id: 'cycle.page.plan.projectionNote',
    defaultMessage: 'Агроцикл ещё не начат: его план — шаблон культуры, сроки работ правят в шаблоне.',
    description: 'Подпись менеджеру под календарём не начатого агроцикла: почему сдвигать работы на нём нельзя',
  },
  openTemplate: { id: 'cycle.page.plan.openTemplate', defaultMessage: 'Открыть шаблон', description: 'Ссылка менеджеру под календарём не начатого агроцикла: перейти к шаблону культуры' },
  rowFigure: { id: 'cycle.page.plan.rowFigure', defaultMessage: 'выполнено {pct}', description: 'Подсказка у доли выполненного в строке работы на календаре; {pct} — «78,0 %»' },
});

const STAGE_WORD: Record<StageState, PlainMessage> = { done: m.stageDone, active: m.stageActive, ahead: m.stageAhead };
const ITEM_WORD: Record<ItemState, PlainMessage> = {
  done: m.itemDone,
  active: m.itemActive,
  soon: m.itemSoon,
  ahead: m.itemAhead,
  cancelled: m.itemCancelled,
};
const MIN_BAND = 0.12;
const ITEM_ICON = { done: CircleCheck, active: CircleDot, soon: Clock3, ahead: CircleDashed, cancelled: Ban } as const;
/** Столько работ видно без раскрытия этапов; больше — раскрыт только идущий этап. */
const OPEN_ALL_UP_TO = 14;

function pctOf(value: number | null): string | null {
  return value === null ? null : pctAxis(value * 100);
}

type Mode = 'brief' | 'detail';
const MODE_KEY = 'agroai.cyclePlan.mode';
function readMode(): Mode {
  try {
    return globalThis.localStorage?.getItem(MODE_KEY) === 'detail' ? 'detail' : 'brief';
  } catch {
    return 'brief';
  }
}

/** Окно работы строкой: «20 сен — 25 окт 2026», у метки — одна дата, у примерного — «≈». */
function itemWindow(item: PlanItem, t: ReturnType<typeof useT>): string {
  if (!item.from) return t(m.stageNoDates);
  const text = item.to && item.to !== item.from ? windowLabel(item.from, item.to) : dateShort(item.from);
  return item.approximate ? t(m.itemApprox, { window: text }) : text;
}

/** Раскрытые этапы по умолчанию: все, пока работ немного, иначе — идущий. */
function initialCollapsed(stages: StageCard[]): ReadonlySet<string> {
  const total = stages.reduce((n, s) => n + s.items.length, 0);
  if (total <= OPEN_ALL_UP_TO) return new Set();
  const open = new Set(stages.filter((s) => s.state === 'active').map((s) => s.key));
  const focus = focusStage(stages);
  if (focus) open.add(focus);
  return new Set(stages.filter((s) => !open.has(s.key)).map((s) => s.key));
}

export function CyclePlanWidget({
  cycleId,
  agro,
  works,
  asOf,
  events,
  editSignal = 0,
  defaultMode,
}: {
  cycleId: string;
  agro: AgrocycleState | null;
  works: Work[];
  asOf: string | null;
  /** Счётчик «Править план» из шапки: рост — подробный вид и прокрутка к плану. */
  editSignal?: number;
  /** Лента событий агроцикла — в конце подробного вида. */
  events?: ReactNode;
  /** Вид при открытии (ответ ИИ); без него — запомненный выбор участника. */
  defaultMode?: Mode;
}) {
  const t = useT();
  const auth = useAuth();
  const mayEdit = auth.principal?.can_edit_agrocycle ?? false;
  const canEdit = mayEdit && agro?.detail?.status === 'started';
  const detail = agro?.detail ?? null;
  const built = useMemo(() => buildStages(detail?.plan.stages, works, asOf, detail?.stage_progress), [detail, works, asOf]);
  const axis = useMemo(
    () => makeAxis([...built.flatMap((s) => [s.from, s.to]), detail?.plan_from ?? null, detail?.plan_to ?? null]),
    [built, detail],
  );
  // Наименьшая ширина полосы — 96 px на шкале 860 px: подпись этапа читается.
  const { stages, lanes } = useMemo(() => {
    const lanes = axis
      ? assignLanes(built, (s) => {
          const left = axis.at(s.from) ?? 0;
          const right = axis.at(s.to ?? s.from) ?? 1;
          return { left, right: Math.max(right, left + MIN_BAND) + 0.004 };
        })
      : 0;
    return { stages: built, lanes };
  }, [built, axis]);
  const [mode, setModeState] = useState<Mode>(() => defaultMode ?? readMode());
  const [target, setTarget] = useState<string | null>(null);
  const [collapsedState, setCollapsed] = useState<ReadonlySet<string> | null>(null);
  const collapsed = collapsedState ?? initialCollapsed(stages);
  const setMode = (next: Mode) => {
    setModeState(next);
    try {
      globalThis.localStorage?.setItem(MODE_KEY, next);
    } catch {
      /* память вида — удобство, без неё работает */
    }
  };
  const expand = (key: string) => {
    if (collapsed.has(key)) {
      const next = new Set(collapsed);
      next.delete(key);
      setCollapsed(next);
    }
  };
  const toggle = (key: string) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setCollapsed(next);
  };
  const openStage = (key: string) => {
    setMode('detail');
    expand(key);
    setTarget(key);
  };
  // «Править план» в шапке: подробный вид (правка живёт на календаре) и
  // прокрутка к текущему этапу, иначе к началу плана.
  useEffect(() => {
    if (!editSignal) return;
    setMode('detail');
    const focus = focusStage(built);
    if (focus) {
      expand(focus);
      setTarget(focus);
    } else document.getElementById(`cycle-plan-${cycleId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSignal]);
  // Этап, выбранный на шкале, — в зону видимости, когда календарь отрисован.
  useEffect(() => {
    if (mode !== 'detail' || !target) return;
    const stage = stages.find((s) => s.key === target);
    const first = stage?.items[0];
    const el = first ? document.querySelector<HTMLElement>(`[data-bar="${CSS.escape(first.key)}"]`) : null;
    (el ?? document.getElementById(`cycle-plan-${cycleId}`))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTarget(null);
  }, [mode, target, cycleId, stages]);

  const counts = useMemo(() => {
    const items = stages.flatMap((s) => s.items).filter((i) => i.state !== 'cancelled');
    return {
      total: items.length,
      done: items.filter((i) => i.state === 'done').length,
      active: items.filter((i) => i.state === 'active').length,
      ahead: items.filter((i) => i.state === 'ahead' || i.state === 'soon').length,
    };
  }, [stages]);

  const info = detail ? planInfo(detail) : null;
  const undated = stages.filter((s) => s.lane < 0);
  const todayPos = axis?.at(asOf) ?? null;
  const hrefFor = (item: PlanItem): string | null => {
    const id = item.work?.id ?? item.plan?.campaign_id;
    return id != null ? `/cycles/${encodeURIComponent(cycleId)}/works/${id}` : null;
  };

  const byBar = useMemo(() => {
    const map = new Map<string, { item: PlanItem; stage: StageCard }>();
    for (const stage of stages) for (const item of stage.items) map.set(item.key, { item, stage });
    return map;
  }, [stages]);

  /** Подписи работы для ящика: окно, состояние, ход, ссылка. */
  const factsOf = (workId: number): WorkFacts | null => {
    const entry = byBar.get(barKey(workId));
    if (!entry) return null;
    const { item, stage } = entry;
    const w = item.work;
    const started = !!w && w.status !== 'upcoming';
    const overdue = w && w.status === 'done' && w.totals.overdue ? w.totals.overdue : null;
    const progress = started
      ? [t(m.itemReported, { pct: pctOf(item.done) ?? '—' }), overdue ? t(m.itemOverdue, { count: countOf(overdue, WORDS.obligation) }) : null].filter(Boolean).join(' · ')
      : item.state === 'cancelled'
        ? null
        : w
          ? t(m.itemAnnounced)
          : t(m.itemNoCampaign);
    return {
      stage: t(m.stageHead, { order: stage.order, name: stage.name }),
      window: itemWindow(item, t),
      state: t(ITEM_WORD[item.state]),
      progress,
      href: hrefFor(item),
      tags: [
        item.plan?.irrigation_no ? t(m.itemIrrigation, { no: item.plan.irrigation_no }) : null,
        item.plan?.origin === 'added' ? t(m.itemAdded) : null,
        item.plan && !item.plan.is_required ? t(m.itemOptional) : null,
      ].filter(Boolean) as string[],
    };
  };

  // Хук правки — до любых ранних возвратов: порядок хуков не должен зависеть от плана.
  const edit = usePlanEditing({ agro, editable: canEdit, factsOf });
  const navigate = useNavigate();

  const groups = useMemo<TimelineGroup[]>(() => {
    const planWorks = new Map<number, { label: string }>();
    for (const { item } of byBar.values()) if (item.plan) planWorks.set(item.plan.id, { label: item.name });
    const hintOf = (item: PlanItem): string | undefined => {
      const timing = item.plan?.timing;
      const hints: string[] = [];
      if (timing?.start_kind === 'after' && timing.start_after != null) {
        const anchor = planWorks.get(timing.start_after)?.label;
        if (anchor) {
          const days = timing.start_after_days ?? 0;
          const fromStart = timing.start_after_edge === 'start';
          const message = days < 0 ? (fromStart ? m.hintBeforeStart : m.hintBeforeEnd) : fromStart ? m.hintAfterStart : m.hintAfterEnd;
          hints.push(t(message, { days: Math.abs(days), name: anchor }));
        }
      }
      if (canEdit && item.plan?.campaign_id != null && item.state !== 'cancelled' && item.state !== 'done') hints.push(t(m.hintLaunched));
      return hints.length ? hints.join(' · ') : undefined;
    };
    return stages.map((stage) => {
      const pct = pctOf(stage.done);
      return {
        id: stage.key,
        label: t(m.stageHead, { order: stage.order, name: stage.name }),
        summary: [countOf(stage.items.length, WORDS.work), pct].filter(Boolean).join(' · '),
        empty: t(m.noItems),
        bars: stage.items.map<TimelineBar>((item) => {
          const win = edit.overlay[item.key];
          const timing = item.plan?.timing;
          const anchorKey = timing?.start_kind === 'after' && timing.start_after != null ? barKey(timing.start_after) : null;
          return {
            id: item.key,
            label: item.name,
            from: win ? win.from : item.from,
            to: win ? win.to : item.to,
            drag: item.plan ? workDrag(item.plan, !!item.from) : 'fixed',
            approximate: item.approximate,
            optional: !!item.plan && !item.plan.is_required,
            changed: !!win,
            removed: item.state === 'cancelled',
            tone: item.state === 'done' ? 'ok' : item.state === 'cancelled' ? 'muted' : 'default',
            link: anchorKey && byBar.has(anchorKey) ? { toId: anchorKey, edge: timing?.start_after_edge === 'start' ? 'start' : 'end' } : undefined,
            hint: hintOf(item),
          };
        }),
      };
    });
  }, [stages, byBar, edit.overlay, canEdit, t]);

  if (stages.length === 0) {
    return (
      <Card size="lg" title={t(m.title)} info={info?.info}>
        <EmptyState compact title={t(m.empty)} reason={info?.empty ?? ''} />
      </Card>
    );
  }

  const stageByKey = new Map(stages.map((s) => [s.key, s]));

  return (
    <Card
      id={`cycle-plan-${cycleId}`}
      size="lg"
      className="cpw"
      title={info && detail?.plan.mode === 'projection' ? info.title : t(m.title)}
      info={info?.info}
      aside={
        <div className="seg" role="group" aria-label={t(m.modeAria)}>
          <button type="button" className={`seg__btn focus-ring ${mode === 'brief' ? 'is-on' : ''}`} aria-pressed={mode === 'brief'} onClick={() => setMode('brief')}>
            <Icon icon={GanttChart} size={16} /> {t(m.modeBrief)}
          </button>
          <button type="button" className={`seg__btn focus-ring ${mode === 'detail' ? 'is-on' : ''}`} aria-pressed={mode === 'detail'} onClick={() => setMode('detail')}>
            <Icon icon={Rows3} size={16} /> {t(m.modeDetail)}
          </button>
        </div>
      }
    >
      <p className="cpw-summary t-caption">
        {t(m.aside, {
          stages: t(m.stageCount, { count: stages.length }),
          works: countOf(counts.total, WORDS.work),
          done: String(counts.done),
          active: String(counts.active),
          ahead: String(counts.ahead),
        })}
      </p>
      {axis ? (
        <div className="cpw-scroll">
          <div className="cpw-axis" role="group" aria-label={t(m.axisAria)} style={{ '--lanes': Math.max(1, lanes) } as CSSProperties}>
            <div className="cpw-months" aria-hidden="true">
              {axis.months.map((month) => (
                <span key={month.iso} style={{ left: `${month.pos * 100}%` }}>{dateShort(month.iso).replace(/^\d+[\s-]*/, '')}</span>
              ))}
            </div>
            <div className="cpw-lanes">
              {axis.months.map((month) => (
                <i key={month.iso} className="cpw-grid" style={{ left: `${month.pos * 100}%` }} aria-hidden="true" />
              ))}
              {stages.filter((s) => s.lane >= 0).map((stage) => {
                const left = axis.at(stage.from) ?? 0;
                const right = axis.at(stage.to ?? stage.from) ?? 1;
                const pct = pctOf(stage.done);
                return (
                  <button
                    key={stage.key}
                    type="button"
                    className="cpw-band focus-ring"
                    data-state={stage.state}
                    title={t(m.stageBarTitle, { name: stage.name, window: windowLabel(stage.from, stage.to), state: t(STAGE_WORD[stage.state]) })}
                    style={{ left: `${left * 100}%`, width: `${Math.max(right - left, MIN_BAND) * 100}%`, top: `calc(${stage.lane} * var(--cpw-lane))` }}
                    onClick={() => openStage(stage.key)}
                  >
                    <i className="cpw-band__fill" style={{ width: `${Math.round((stage.done ?? 0) * 100)}%` }} aria-hidden="true" />
                    <span className="cpw-band__name">{stage.name}</span>
                    {pct ? <span className="cpw-band__pct num">{pct}</span> : null}
                  </button>
                );
              })}
              {todayPos !== null ? (
                <span className="cpw-today" style={{ left: `${todayPos * 100}%` }}>
                  <span>{t(m.today, { date: dateShort(asOf) })}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {undated.length ? <p className="t-caption">{t(m.noDatesNote, { names: undated.map((s) => s.name).join(', ') })}</p> : null}

      {mode === 'brief' ? (
        <p className="t-caption cpw-hint">{t(m.briefHint)}</p>
      ) : (
        <div className="cpw-detail">
          <p className="t-caption cpt-legend">
            {t(m.legend)} {canEdit ? t(m.legendEdit) : null}
            {mayEdit && detail?.status === 'not_started' ? (
              <>
                {' '}
                {t(m.projectionNote)}{' '}
                <Link to={`/ops/templates?template=${detail.template_id}`}>{t(m.openTemplate)}</Link>
              </>
            ) : null}
          </p>
          <WindowTimeline
            groups={groups}
            ariaLabel={t(m.timelineAria)}
            editable={canEdit}
            collapsed={collapsed}
            onToggleGroup={toggle}
            selectedId={edit.selectedId}
            onSelect={(id) => {
              const entry = byBar.get(id);
              if (entry?.item.plan) edit.select(id);
              else {
                const href = entry ? hrefFor(entry.item) : null;
                if (href) navigate(href);
              }
            }}
            onShift={canEdit ? edit.onShift : undefined}
            previewShift={canEdit ? edit.previewShift : undefined}
            renderLabel={(bar) => {
              const item = byBar.get(bar.id)?.item;
              return (
                <span className="cpt-name" data-state={item?.state}>
                  {item ? <Icon icon={ITEM_ICON[item.state]} size={16} /> : null}
                  <span className="wt__text">{bar.label}</span>
                </span>
              );
            }}
            renderRowActions={(bar) => {
              const item = byBar.get(bar.id)?.item;
              const started = !!item?.work && item.work.status !== 'upcoming';
              const pct = started && item ? pctOf(item.done) : null;
              return pct ? <span className="cpt-fig num" title={t(m.rowFigure, { pct })}>{pct}</span> : null;
            }}
            renderGroupActions={
              canEdit
                ? (group) => {
                    const card = stageByKey.get(group.id);
                    return card?.plan ? (
                      <button
                        type="button"
                        className="cpt-add focus-ring"
                        aria-label={t(m.addWork, { stage: card.name })}
                        title={t(m.addWork, { stage: card.name })}
                        onClick={() => edit.addTo(card.plan!.id)}
                      >
                        <Icon icon={Plus} size={16} />
                      </button>
                    ) : null;
                  }
                : undefined
            }
          />
          {events ? <div className="cpw-events">{events}</div> : null}
        </div>
      )}
      {edit.node}
    </Card>
  );
}

/** Для гида и тестов: подпись состояния карточки работы. */
export const itemStateLabel = (state: ItemState): string => tr(ITEM_WORD[state]);
